import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Chevron } from '@/components/Ornaments';
import { ManhattanLockup } from '@/components/ManhattanLockup';
import { useVenue } from '@/hooks/useVenue';
import { api } from '@/lib/api';
import { formatDateLong, formatTime } from '@/lib/format';
import { useT, useLocale, usePick } from '@/i18n';
import type { Event } from '@/types/event';

const HOW_STEPS = [
  {
    n: '1',
    t: 'landing.howitworks.step1.title',
    d: 'landing.howitworks.step1.description',
  },
  {
    n: '2',
    t: 'landing.howitworks.step2.title',
    d: 'landing.howitworks.step2.description',
  },
  {
    n: '3',
    t: 'landing.howitworks.step3.title',
    d: 'landing.howitworks.step3.description',
  },
  {
    n: '4',
    t: 'landing.howitworks.step4.title',
    d: 'landing.howitworks.step4.description',
  },
] as const;

export default function Landing() {
  const t = useT();
  const locale = useLocale();
  const pick = usePick();
  const venue = useVenue();
  const [next, setNext] = useState<Event | null>(null);
  // Horas distintas de las sesiones PUBLICADAS (el endpoint ya filtra a
  // published + futuras), en la zona horaria del local. Sustituye al rótulo fijo.
  const [sessionTimes, setSessionTimes] = useState('');
  // Todas las horas de la noche destacada (misma fecha que el evento destacado).
  const [nextTimes, setNextTimes] = useState('');

  useEffect(() => {
    if (venue.status !== 'ready') return;
    const tz = venue.venue.timezone || 'Europe/Madrid';
    // Hora "HH:MM" (con dos puntos) en la zona del local.
    const hhmm = (iso: string) =>
      new Date(iso).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: tz,
      });
    // Ordenar por el instante real (ISO en UTC) y formatear después: así
    // medianoche no se cuela como "24:00" ni se desordena.
    const uniqSorted = (list: Event[]) => {
      const ordered = [...list].sort((a, b) =>
        a.starts_at.localeCompare(b.starts_at)
      );
      return [...new Set(ordered.map((e) => hhmm(e.starts_at)))].join(' - ');
    };

    api
      .get<Event[]>('/api/v1/events', { params: { venue_id: venue.venue.id } })
      .then((res) => {
        // Featured card = the nearest upcoming event. The API returns published
        // events sorted by start time, so the first future one is the closest.
        const now = Date.now();
        const upcoming = res.data
          .filter((e) => new Date(e.starts_at).getTime() >= now)
          .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
        const featured = upcoming[0] ?? res.data[0] ?? null;
        setNext(featured);

        // Rótulo SESIONES: todas las horas publicadas.
        setSessionTimes(uniqSorted(res.data));

        // Tarjeta destacada: todas las horas de esa misma noche (fecha).
        setNextTimes(
          featured
            ? uniqSorted(res.data.filter((e) => e.date === featured.date))
            : ''
        );
      });
  }, [venue]);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 overflow-x-clip">
        {/* Hero */}
        <section className="pt-5 pb-0 text-center overflow-x-clip">
          <Chevron className="mx-auto mb-3" />
          <ManhattanLockup className="mx-auto w-[78%] max-w-[300px] text-ink-body" />
          <p
            className="font-deco text-gold text-[clamp(1.35rem,6vw,2.25rem)] tracking-[0.02em] uppercase leading-none mt-1.5"
            style={{
              WebkitTextStrokeWidth: '0.6px',
              WebkitTextStrokeColor: 'currentColor',
            }}
          >
            Jazz Nights
          </p>
          <p className="text-gold font-serif uppercase tracking-[0.05em] text-[clamp(1.05rem,4.3vw,1.5rem)] leading-none mt-1.5">
            {t('landing.hero.price')}
          </p>

          <div className="rule my-7" />

          <p className="text-ink-body text-center text-sm font-medium mx-auto leading-snug">
            {t('landing.hero.subtitle')
              .split('\n')
              .map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {line}
                </span>
              ))}
          </p>

          <div className="mt-4">
            <a
              href="#programacion"
              className="text-gold text-xs font-medium tracking-[0.12em] uppercase hover:text-gold-soft"
            >
              {t('landing.hero.cta')}
            </a>
            <span className="block mx-auto mt-2 h-px w-28 bg-gold/60" />
          </div>

          <div className="rule mt-6 mb-0" />
        </section>

        {/* Info block */}
        <section className="mt-7 space-y-6 text-center">
          <InfoItem
            label={t('landing.info.sessions')}
            value={sessionTimes || t('landing.info.sessions.value')}
          />
          <InfoItem
            label={t('landing.info.duration')}
            value={t('landing.info.duration.value')}
          />
          <InfoItem
            label={t('landing.info.includes')}
            value={t('landing.info.includes.value')}
          />
        </section>

        {/* Primary CTA */}
        <Link
          to="/sesiones"
          className="mx-auto my-9 block w-fit px-8 py-3.5 uppercase tracking-widest text-xs bg-gold text-ink text-center"
        >
          {t('landing.button.reserve')}
        </Link>

        {/* Programación */}
        <section id="programacion" className="pt-2 pb-4">
          <div className="rule mb-5" />
          <h2 className="font-display text-gold text-3xl text-center uppercase mb-4">
            {t('landing.programming.title')}
          </h2>

          {next && (
            <Link to="/sesiones" className="block border border-line p-3">
              <div className="aspect-[16/10] w-full overflow-hidden bg-ink-soft flex items-center justify-center text-ink-muted text-xs italic">
                {next.artist_photo_url ? (
                  <img
                    src={next.artist_photo_url}
                    alt={next.artist_name ?? ''}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  t('landing.programming.photo.placeholder')
                )}
              </div>
              <div className="pt-4 pb-1">
                <h3
                  className="font-display text-2xl leading-none text-gold uppercase tracking-[0.04em]"
                  style={{ WebkitTextStrokeWidth: '0.4px', WebkitTextStrokeColor: 'currentColor' }}
                >
                  {next.artist_name ?? t('landing.programming.artist.pending')}
                </h3>
                <p className="text-ink-muted text-sm leading-none mt-0.5">
                  {formatDateLong(next.starts_at, locale)} ·{' '}
                  {nextTimes || formatTime(next.starts_at)}
                </p>
                {pick(next.artist_description_i18n) && (
                  <p className="text-ink-body text-sm font-medium leading-snug mt-3">
                    {pick(next.artist_description_i18n)}
                  </p>
                )}
              </div>
            </Link>
          )}
        </section>

        {/* Cómo funciona */}
        <section className="pt-3 pb-14">
          <div className="rule mb-5" />
          <h2 className="font-display text-gold text-3xl text-center uppercase mb-5">
            {t('landing.howitworks.title')}
          </h2>
          <ol className="space-y-3.5">
            {HOW_STEPS.map((s) => (
              <li key={s.n} className="text-center">
                <Chevron className="mx-auto mb-1.5" />
                <p className="text-ink-body font-deco text-base leading-none">
                  {s.n}
                </p>
                <p className="font-display text-xl text-ink-body uppercase leading-none mt-1.5">
                  {t(s.t)}
                </p>
                <p className="text-ink-body text-sm font-semibold leading-snug max-w-xs mx-auto mt-2">
                  {t(s.d)}
                </p>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </Layout>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p
        className="font-display text-2xl leading-none tracking-[0.06em] uppercase text-ink-body"
        style={{ WebkitTextStrokeWidth: '0.5px', WebkitTextStrokeColor: 'currentColor' }}
      >
        {label}
      </p>
      <p className="font-serif text-base leading-none text-ink-body mt-1.5">{value}</p>
    </div>
  );
}
